import { Injectable } from '@angular/core';
import { PORTAL_PREFERENCES } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { PortalRepository } from '@app/core/repositories/portal-repository';
import { RouterService } from '@app/routing/router.service';
import { noop } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, first, map, take } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class PreferenceStorageService {
  private logger = loggerFor('PreferenceStorageService');

  private _preferences$ = new ReplaySubject<Record<string, any>>(1);
  private serverTokens: string[] = ['preferredLanguage'];

  /**
   * Application user preferences that are stored in browser local storage
   */
  get preferences$() {
    return this._preferences$.asObservable();
  }

  constructor(
    private authService: AuthenticationService,
    private localStorageService: LocalStorageService,
    private portalRepository: PortalRepository,
    private routerService: RouterService
  ) {
    const preferences: StringMap<any> = this.localStorageService.getItem(PORTAL_PREFERENCES) ?? {};
    this._preferences$.next(preferences);

    // just for logging
    this.preferences$.subscribe((current) =>
      this.logger.debug('Current user application preferences in storage', current)
    );

    // testing if preference$ will emit preferred language when it changes for auth-user (YES it does!)
    // this.preference$<string>('preferredLanguage', 'de').subscribe((value) =>
    //   this.logger.warn('PreferredLanguage', value)
    // )
  }

  /**
   * Will emit values for `name` matching a preference in following order:
   * - `route-name.preference-name`,
   * - `preference-name`
   *
   * Values emitted will be those found in storage and in auth-user preferences (which overrule those from storage).
   *
   * @param name of the preference you are interested in
   * @param defaultValue if no value is found then this is returned
   * @returns Observable emitting values for the specified preference.
   */
  preference$<T>(name: string, defaultValue?: T): Observable<T> {
    const routeName$: Observable<string> = of(this.getCurrentRouteName());

    return combineLatest([
      routeName$,
      this.preferences$,
      this.authService.authUserPortalPreferences.data$,
    ]).pipe(
      map(([routeName, preferences, authUserPreferences]) => {
        const map = {
          ...preferences,
          ...authUserPreferences,
        };

        return map[`${routeName}.${name}`] ?? map[name] ?? defaultValue;
      }),
      distinctUntilChanged()
    );
  }

  update<T>(name: string, callback: (currentValue: T | undefined) => T, prefixRouteName?: boolean): void {
    this.preference$<T>(name)
      .pipe(first())
      .subscribe((currentValue) => {
        const newValue = callback(currentValue);
        this.updatePreference(name, newValue, prefixRouteName);
      });
  }

  /**
   * Updating/adding user's application preference to the browser local storage.
   * If `prefixRouteName` is `true` then the value will be saved under `route-name.preference-name`.
   *
   * @param name of the preference
   * @param value
   * @param prefixRouteName default is `false`
   */
  updatePreference(name: string, value: any, prefixRouteName: boolean = false) {
    this.logger.debug(`Update preference '${name}'=${value}, prefixRouteName=${prefixRouteName}`);

    const prefixedName = this.prefixName(prefixRouteName, name);

    if (this.serverTokens.includes(name)) {
      // Save to server
      this.authService.authUserPortalPreferences.data$.pipe(first()).subscribe((preferences) => {
        this.portalRepository
          .updatePreferences({
            ...preferences,
            [name]: value,
          })
          .subscribe(noop);
      });
    }

    this.preferences$.pipe(take(1)).subscribe((currentPreferences) => {
      const preferences = {
        ...currentPreferences,
        [prefixedName]: value,
      };

      this.localStorageService.setItem('PORTAL_PREFERENCES', preferences);
      this._preferences$.next(preferences);
    });
  }

  private getCurrentRouteName(): string {
    return this.routerService.currentRouteData?.name;
  }

  private prefixName(prefixRouteName: boolean, name: string): string {
    let prefix: string;

    if (prefixRouteName) {
      const routeName = this.getCurrentRouteName();

      if (routeName == null) {
        throw new Error('Cannot save preference because the route name cannot be determined');
      }

      prefix = `${routeName}.${name}`;
    } else {
      prefix = name;
    }

    return prefix;
  }
}
